import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { ILiveTableOptions } from '@/interfaces/powerMarkets/table/ILiveTableOptions.ts';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ITablePushData } from '@/interfaces/powerMarkets/table/ILiveTablePushData.ts';
import {
    ILiveTableDisplaySettings,
    applyLiveTablePushDisplaySettings,
    updateTableDataWithPush,
} from '@/helpers/liveTable/liveTableMethods.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { getSelectedPlantGroupColourSettings, getSelectedPlantIds } from '@/helpers/powerMarkets/plantGroupSettings.ts';
import { cloneDeep } from '@/utils/global.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { IComponentTableData } from './IComponentTableData.ts';

const STORE_KEY_SEPARATOR = '::';

export const useLiveTableStore = defineStore('liveTableStore', () => {
    const storeTables = ref<Record<string, IGenericTableData>>({});
    const componentTables = ref<Record<string, IComponentTableData>>({});
    const liveTableOptions = ref<Record<string, ILiveTableOptions>>({});
    const dataStore = useDataStore();
    const uiStore = useUIStore();
    const userSettingsStore = useUserSettingsStore();

    function getLiveTableStoreKey (
        moduleType: string,
        country: string,
        selection?: {
            day?: Date | string;
            period?: number;
        },
    ): string {
        if (!selection) return moduleType;

        const keyParts = [moduleType, country.toLowerCase()];
        if (selection.day) keyParts.push(dayjs(selection.day).format('YYYY-MM-DD'));
        if (selection.period !== undefined) keyParts.push(String(selection.period));
        return keyParts.join(STORE_KEY_SEPARATOR);
    }

    function updateAllLiveTableOptions (newOptions: Record<string, ILiveTableOptions>): void {
        liveTableOptions.value = newOptions;
    }

    function handleTablePush (pushData: ITablePushData): void {
        const liveTableOption = liveTableOptions.value[pushData.tableType];
        if (!liveTableOption) return;

        const liveTableDisplaySettings: ILiveTableDisplaySettings = {
            bidColour: userSettingsStore.userSettings.otherSettings.data.bidColour,
            offerColour: userSettingsStore.userSettings.otherSettings.data.offerColour,
            bingoNamesToEnums: dataStore.staticStore.bingoNamesToEnums,
            bingoColourOverrides: userSettingsStore.userSettings.otherSettings.data.bingoColourOverrides,
            useDarkHeatmapColours: uiStore.isDarkTheme,
            selectedPlantGroupColourSettings: getSelectedPlantGroupColourSettings(userSettingsStore.userSettings.otherSettings.data),
            selectedPlantIds: getSelectedPlantIds(userSettingsStore.userSettings.otherSettings.data),
        };
        applyLiveTablePushDisplaySettings(pushData.changes, liveTableOption.moduleType, liveTableDisplaySettings);

        if (liveTableOption.useStore) {
            updateTableInStore({ data: pushData, moduleType: liveTableOption.moduleType });
        } else {
            updateComponentData(pushData, liveTableOption);
        }
    }

    function pushOverlapsDateRange (data: ITablePushData, dateRange: IDateRange): boolean {
        if (!data.extraInfo?.fromDate || !data.extraInfo.toDate) return false;

        const pushFrom = dayjs(data.extraInfo.fromDate);
        const pushTo = dayjs(data.extraInfo.toDate);
        const selectedFrom = dayjs(dateRange.from).startOf('day');
        const selectedTo = dayjs(dateRange.to).startOf('day');

        const pushIsBeforeRange = pushFrom.isBefore(selectedFrom) && pushTo.isBefore(selectedFrom);
        const pushIsAfterRange = pushFrom.isAfter(selectedTo) && pushTo.isAfter(selectedTo);
        return !pushIsBeforeRange && !pushIsAfterRange;
    }

    function updateComponentData (data: ITablePushData, liveTableOption: ILiveTableOptions): void {
        Object.keys(componentTables.value).forEach((tableId: string) => {
            const table = componentTables.value[tableId];
            if (table.name !== liveTableOption.moduleType || table.country !== data.country) return;
            if (liveTableOption.getOnDayChange
                && (!data.extraInfo?.day || !table.datePeriod || !dayjs(data.extraInfo.day).isSame(table.datePeriod.date, 'day'))) return;
            if (liveTableOption.getOnPeriodChange
                && (!table.datePeriod || table.datePeriod.period !== data.extraInfo?.period)) return;
            if (liveTableOption.getOnDayRangeChange && table.dateRange && !pushOverlapsDateRange(data, table.dateRange)) return;

            table.pushes.push(cloneDeep(data.changes));
            table.reRequest = data.reRequestTable;
        });
    }

    function addTableToStore (moduleType: string, tableData: IGenericTableData, countryId?: string, storeKey: string = moduleType): void {
        tableData.country = countryId;
        tableData.reRequestCounter = 0;
        storeTables.value[storeKey] = tableData;
    }

    function addTableOnScreen (
        id: string,
        moduleType: string,
        countryId?: string,
        dateRange?: IDateRange,
        datePeriod?: IDatePeriod,
        storeKey?: string,
    ): void {
        const previousStoreKey = componentTables.value[id]?.storeKey;
        componentTables.value[id] = {
            name: moduleType,
            pushes: [],
            reRequest: false,
            country: countryId,
            dateRange,
            datePeriod,
            storeKey,
        };
        clearUnusedStoredTable(previousStoreKey);
    }

    function clearUnusedStoredTable (storeKey?: string): void {
        if (!storeKey || Object.values(componentTables.value).some(table => table.storeKey === storeKey)) return;
        delete storeTables.value[storeKey];
    }

    function removeTableOnScreen (id: string): void {
        const storeKey = componentTables.value[id]?.storeKey;
        delete componentTables.value[id];
        clearUnusedStoredTable(storeKey);
    }

    function clearComponentTables (): void {
        componentTables.value = {};

        Object.keys(storeTables.value).forEach((moduleLiveTable) => {
            if (moduleLiveTable.includes('otification')) return;
            delete storeTables.value[moduleLiveTable];
        });
    }

    function findStoredTableKeyForPush (moduleType: string, pushData: ITablePushData): string | undefined {
        const liveTableOption = Object.values(liveTableOptions.value).find(option => option.moduleType === moduleType);
        const pushSelection = liveTableOption?.getOnDayChange || liveTableOption?.getOnPeriodChange ? {
            day: liveTableOption.getOnDayChange ? pushData.extraInfo?.day : undefined,
            period: liveTableOption.getOnPeriodChange ? pushData.extraInfo?.period : undefined,
        } : undefined;
        const selectedStoreKey = pushSelection
            ? getLiveTableStoreKey(moduleType, pushData.country, pushSelection)
            : moduleType;

        const table = storeTables.value[selectedStoreKey];
        return table?.country?.toLowerCase() === pushData.country.toLowerCase()
            ? selectedStoreKey
            : undefined;
    }

    function updateTableInStore (data: { moduleType: string; data: ITablePushData }): void {
        const storeTableKey = findStoredTableKeyForPush(data.moduleType, data.data);

        if (!storeTableKey) return;

        const tableToUpdate = storeTables.value[storeTableKey];

        const liveTableOption = Object.values(liveTableOptions.value).find(option => option.moduleType === data.moduleType);
        const updateResponse = updateTableDataWithPush(tableToUpdate, data.data.changes, liveTableOption?.maxEntriesOnTable ?? null);

        if (data.data.reRequestTable || !updateResponse.success) {
            if (!tableToUpdate.reRequestCounter) {
                tableToUpdate.reRequestCounter = 0;
            }
            tableToUpdate.reRequestCounter += 1;
        }
    }

    function markNotificationsSeenLocally (): void {
        storeTables.value.Notifications?.data.forEach((notification) => {
            notification.row.Unseen.values = ['True'];
            notification.row.Unseen.backgroundColour = null;
        });
    }

    function deleteLastHourNotifications (deleteTime: number): void {
        const notificationsTable = storeTables.value.Notifications;
        if (!notificationsTable) return;

        const firstTime = notificationsTable.data.findIndex((notification) => {
            const time = notification.row.Time.values[0];
            return typeof time === 'number' && time < deleteTime;
        });
        if (firstTime === -1) return;

        notificationsTable.data.splice(firstTime);
    }

    return {
        storeTables,
        componentTables,
        liveTableOptions,
        getLiveTableStoreKey,
        updateAllLiveTableOptions,
        handleTablePush,
        addTableOnScreen,
        removeTableOnScreen,
        clearComponentTables,
        addTableToStore,
        updateTableInStore,
        markNotificationsSeenLocally,
        deleteLastHourNotifications,
    };
});
